import {
  CURRENCIES_META,
  CurrencyCodeType,
} from '@mint-lib/currencies-helpers';
import { TFunction } from '@mint-lib/i18n';
import camelcase from 'camelcase';

import { SVG } from '@myn/resources';

import {
  BUILDER_ROW_DEPTH,
  BUILDER_ROW_TYPES,
  getMapFormatValueToName,
  getMapTypeValueToName,
  ROW_STATUSES,
} from '../constants/campaignBuilder.js';
import {
  customColumnFormatOptions,
  customColumnTypeOptions,
} from '../constants/editModal.js';
import { getTooltipTextConditions } from '../constants/insertionOrder.jsx';
import { BuilderRowType, ChannelRowType } from '../pages/Builder/types.js';
import {
  CustomColumnFormat,
  CustomColumnType,
} from '../types/builderTableView.js';
import type { TooltipConditions } from '../types/insertionOrder.js';
import { IntegrationsType, Platform } from '../types/integrations.js';
import type { CustomError } from '../types/shared.js';
import { transformChannelPlatformName } from './campaignsTransformer.js';
import { getInsertionOrderErrorMessages } from './insertionOrder.jsx';

export const getRowCurrencySign = (
  journeyCurrencySign = '',
  code?: CurrencyCodeType | null,
  isCampaignCurrencyMode?: boolean,
) => {
  if (isCampaignCurrencyMode || !code) {
    return journeyCurrencySign;
  }

  const rowCurrency = CURRENCIES_META[code];
  const rowCurrencySign = rowCurrency
    ? rowCurrency.symbol
    : journeyCurrencySign;

  return rowCurrencySign;
};

export const getRowCurrencyValue = (
  defaultCurrency: string | null = null,
  code?: CurrencyCodeType | null,
  isCampaignCurrencyMode?: boolean,
) => {
  if (isCampaignCurrencyMode || !code) {
    return defaultCurrency;
  }

  return code;
};

export const prepareMediaPlanRows = ({
  rows,
  journeyCurrency,
  metaChannels,
  parentInfo = {},
  predictiveData,
  rowsStatus,
  platformsIntegrations,
  isCampaignCurrencyMode,
}: any) =>
  rows.map((row: any) => {
    const { budget, dateFrom, dateTo } = row.fields;
    const isTileRow = row.type === BUILDER_ROW_TYPES.TILE;
    const isPlannedRow =
      budget !== null && dateFrom !== null && dateTo !== null;

    if (
      isTileRow &&
      ((rowsStatus === ROW_STATUSES.PLANNED && !isPlannedRow) ||
        (rowsStatus === ROW_STATUSES.UNPLANNED && isPlannedRow))
    ) {
      return null;
    }

    const integratedPlatform = platformsIntegrations?.find(
      (platform: Platform) => platform.platform === row.name,
    );
    const logo = integratedPlatform?.logo;
    const label =
      metaChannels?.find((channel: ChannelRowType) => channel.name === row.name)
        ?.label ||
      integratedPlatform?.label ||
      '';
    const supportNotifications =
      integratedPlatform?.type === IntegrationsType.VIRTUAL;

    const rowCurrencySign = getRowCurrencySign(
      journeyCurrency?.sign,
      row.overriddenCurrency,
      isCampaignCurrencyMode,
    );

    const rowCurrencyValue = getRowCurrencyValue(
      journeyCurrency?.value,
      row.overriddenCurrency,
      isCampaignCurrencyMode,
    );

    const subRows = row.subItems
      ? prepareMediaPlanRows({
          rows: row.subItems,
          journeyCurrency,
          metaChannels,
          parentInfo: {
            name: camelcase(row.name),
            dateFrom: row.fields?.dateFrom,
            dateTo: row.fields?.dateTo,
            isGhost: row.isGhost,
          },
          predictiveData,
          rowsStatus,
          platformsIntegrations,
          isCampaignCurrencyMode,
        }).filter((mediaPlanRow: any) => !!mediaPlanRow)
      : [];

    return {
      ...row,
      label,
      currencySign: rowCurrencySign,
      currencyValue: rowCurrencyValue,
      nameKey: row.name,
      fields: null,
      parentInfo,
      subRows,
      subItems: null,
      logo,
      supportNotifications,
      ...row.fields,
      ...predictiveData?.[row.id],
    };
  });

export const addMetaToRows = <T extends Record<string, unknown>>(
  rows: BuilderRowType[],
  meta: T,
): (BuilderRowType & T)[] => {
  return rows.map((row) => {
    if (!row.subRows?.length) {
      return {
        ...row,
        ...meta,
      };
    }

    return {
      ...row,
      ...meta,
      subRows: addMetaToRows(row.subRows, meta),
    };
  });
};

export const getParentRowsIds = (rowId: any, rows: any) =>
  rows.reduce((acc: any, row: any) => {
    if (!row.subItems) {
      if (row.id === rowId) {
        return [rowId];
      }
      return acc;
    }

    if (row.id === rowId) {
      return [rowId];
    }

    const childRows = getParentRowsIds(rowId, row.subItems);
    if (childRows.length) {
      return [...childRows, row.id];
    }
    return acc;
  }, []);

export const getFormatOptions = (t: TFunction) => {
  const mapFormatValueToName = getMapFormatValueToName(t);

  return customColumnFormatOptions.map((option) => ({
    label: mapFormatValueToName[option as CustomColumnFormat],
    value: option,
  }));
};

export const getTypeOptions = (t: TFunction) => {
  const mapTypeValueToName = getMapTypeValueToName(t);

  return customColumnTypeOptions.map((option) => ({
    label: mapTypeValueToName[option as CustomColumnType],
    value: option,
  }));
};

export const getPlatformIcon = (row: any) => {
  const value = row.original.name;
  const preparedValue = transformChannelPlatformName(value);

  if (row.depth === BUILDER_ROW_DEPTH.PLATFORM) {
    return row.original.logo || SVG.customPlatform;
  }

  return (
    SVG.insightsPack.all[preparedValue as keyof typeof SVG.insightsPack.all] ||
    SVG.customPlatform
  );
};

export const getInsertionOrderButtonTooltip = (
  conditions: TooltipConditions,
  t: TFunction,
) => {
  const conditionsWithMessages = getTooltipTextConditions(conditions, t);

  const matchingCondition = conditionsWithMessages.find(
    ({ condition }) => condition,
  );

  return matchingCondition
    ? matchingCondition.message
    : t('Create insertion order');
};

export const handleInsertionOrderError = (error: CustomError, t: TFunction) => {
  const insertionOrderErrorMessages = getInsertionOrderErrorMessages(t);
  type errorsCodes = keyof typeof insertionOrderErrorMessages;

  const message =
    insertionOrderErrorMessages[error?.code as errorsCodes] ||
    insertionOrderErrorMessages.default;

  return {
    code: error?.code ?? '',
    message: message,
  };
};
